/**
 * Inline RGB color picker for particle effect editing.
 * Shows a small swatch + hex input. Click swatch for R/G/B sliders popover.
 * Supports HDR values (>1.0) for bloom pickup.
 * Returns [r, g, b] tuple.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import styles from '../../ui/ParticleEditor.module.css';
import type { RGBColor } from '@warmancer/shared';

interface ForgeColorPickerProps {
  readonly label: string;
  readonly value: RGBColor;
  readonly onChange: (color: RGBColor) => void;
  readonly max?: number;
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

function rgbToHex(r: number, g: number, b: number): string {
  const toHex = (v: number) => Math.round(clamp(v, 0, 1) * 255).toString(16).padStart(2, '0');
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

function hexToRgb(hex: string): RGBColor | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return null;
  return [
    parseInt(m[1].slice(0, 2), 16) / 255,
    parseInt(m[1].slice(2, 4), 16) / 255,
    parseInt(m[1].slice(4, 6), 16) / 255,
  ];
}

export function ForgeColorPicker({ label, value, onChange, max = 2.0 }: ForgeColorPickerProps) {
  const [open, setOpen] = useState(false);
  const [hexInput, setHexInput] = useState(rgbToHex(value[0], value[1], value[2]));
  const popoverRef = useRef<HTMLDivElement>(null);
  const swatchRef = useRef<HTMLDivElement>(null);

  // Sync hex input when value changes externally
  useEffect(() => {
    setHexInput(rgbToHex(value[0], value[1], value[2]));
  }, [value]);

  // Close popover on outside click
  useEffect(() => {
    if (!open) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        popoverRef.current && !popoverRef.current.contains(e.target as Node) &&
        swatchRef.current && !swatchRef.current.contains(e.target as Node)
      ) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleSwatchClick = useCallback(() => {
    setOpen((v) => !v);
  }, []);

  const handleChannelChange = useCallback((channel: 0 | 1 | 2, val: number) => {
    const newColor: RGBColor = [...value];
    newColor[channel] = val;
    onChange(newColor);
  }, [value, onChange]);

  const handleHexBlur = useCallback(() => {
    const rgb = hexToRgb(hexInput);
    if (rgb) {
      onChange(rgb);
    } else {
      setHexInput(rgbToHex(value[0], value[1], value[2]));
    }
  }, [hexInput, value, onChange]);

  const handleHexKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleHexBlur();
  }, [handleHexBlur]);

  const swatchColor = rgbToHex(value[0], value[1], value[2]);
  const channelLabels = ['R', 'G', 'B'] as const;

  return (
    <div className={styles.colorRow}>
      <span className={styles.propertyLabel}>{label}</span>
      <div
        ref={swatchRef}
        className={styles.colorSwatch}
        style={{ backgroundColor: swatchColor }}
        onClick={handleSwatchClick}
      />
      <input
        className={styles.colorHexInput}
        value={hexInput}
        onChange={(e) => setHexInput(e.target.value)}
        onBlur={handleHexBlur}
        onKeyDown={handleHexKeyDown}
      />
      {open && (
        <div ref={popoverRef} className={styles.colorPopover}>
          {channelLabels.map((ch, i) => (
            <div key={ch} className={styles.colorSliderRow}>
              <span className={styles.colorSliderLabel}>{ch}</span>
              <input
                type="range"
                className={styles.colorSlider}
                min={0}
                max={max}
                step={0.01}
                value={value[i]}
                onChange={(e) => handleChannelChange(i as 0 | 1 | 2, Number(e.target.value))}
              />
              <span className={styles.colorSliderValue}>{value[i].toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
